import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Flame, Clock, Target, Trash2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch sessions and stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, statsRes] = await Promise.all([
          axios.get(`${API}/sessions`),
          axios.get(`${API}/sessions/stats`)
        ]);
        setSessions(sessionsRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Delete session
  const handleDelete = async (sessionId) => {
    setDeletingId(sessionId);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      // Refresh stats
      const statsRes = await axios.get(`${API}/sessions/stats`);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Background texture */}
      <div className="fixed inset-0 carbon-texture pointer-events-none" />
      
      {/* Ambient glow */}
      <div className="fixed top-0 left-1/3 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center gap-4">
        <button
          data-testid="back-to-setup-button"
          onClick={() => navigate('/')}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gradient">
            Session History
          </h1>
          <p className="text-zinc-500 text-sm">Track your progress over time</p>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 p-6 max-w-6xl mx-auto space-y-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10">
                      <Flame className="text-cyan-400" size={20} />
                    </div>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Reps</span>
                  </div>
                  <p data-testid="total-reps-stat" className="font-data text-4xl font-bold text-white">
                    {stats.total_reps}
                  </p>
                </div>

                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <Trophy className="text-purple-400" size={20} />
                    </div>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Best Session</span>
                  </div>
                  <p data-testid="best-session-stat" className="font-data text-4xl font-bold text-white">
                    {stats.best_session_reps}
                  </p>
                </div>

                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <CheckCircle className="text-green-400" size={20} />
                    </div>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Goals Hit</span>
                  </div>
                  <p data-testid="goals-achieved-stat" className="font-data text-4xl font-bold text-white">
                    {stats.goals_achieved}
                  </p>
                </div>

                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-yellow-500/10">
                      <Clock className="text-yellow-400" size={20} />
                    </div>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Time</span>
                  </div>
                  <p data-testid="total-time-stat" className="font-data text-4xl font-bold text-white">
                    {stats.total_duration_minutes}m
                  </p>
                </div>
              </div>
            )}

            {/* Sessions List */}
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold text-white uppercase tracking-wide">
                Recent Sessions
              </h2>

              {sessions.length === 0 ? (
                <div className="glass rounded-xl p-12 text-center">
                  <Target className="mx-auto mb-4 text-zinc-600" size={48} />
                  <h3 className="font-heading text-xl font-semibold text-zinc-400 mb-2">
                    No Sessions Yet
                  </h3>
                  <p className="text-zinc-500 mb-6">Complete your first heel raise session to see it here.</p>
                  <button
                    data-testid="start-first-session-button"
                    onClick={() => navigate('/')}
                    className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full transition-transform active:scale-95"
                  >
                    Start First Session
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => {
                    const goalMet = session.reps_completed >= session.goal;
                    return (
                      <div
                        key={session.id}
                        data-testid={`session-item-${session.id}`}
                        className={`session-item ${goalMet ? 'border-green-500/30' : ''}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${goalMet ? 'bg-green-500/10' : 'bg-zinc-800'}`}>
                            {goalMet ? (
                              <CheckCircle className="text-green-400" size={24} />
                            ) : (
                              <Target className="text-zinc-400" size={24} />
                            )}
                          </div>
                          <div>
                            <p className="font-heading text-lg font-semibold text-white uppercase">
                              Heel Raises
                            </p>
                            <p className="text-sm text-zinc-500">
                              {formatDate(session.created_at)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="font-data text-2xl font-bold text-cyan-400">
                              {session.reps_completed}
                            </p>
                            <p className="text-xs text-zinc-500">/{session.goal} reps</p>
                          </div>

                          <div className="text-center">
                            <p className="font-data text-lg text-zinc-300">
                              {formatDuration(session.duration_seconds)}
                            </p>
                            <p className="text-xs text-zinc-500">duration</p>
                          </div>

                          <button
                            data-testid={`delete-session-${session.id}`}
                            onClick={() => handleDelete(session.id)}
                            disabled={deletingId === session.id}
                            className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {deletingId === session.id ? (
                              <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 size={20} />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })})
                </div>
              )}
            </div>

            {/* Start new session CTA */}
            {sessions.length > 0 && (
              <div className="text-center pt-8">
                <button
                  data-testid="start-new-session-button"
                  onClick={() => navigate('/')}
                  className="px-12 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-lg rounded-full transition-transform active:scale-95 neon-cyan"
                >
                  Start New Session
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
